import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { ArrowLeft, Save, Upload, X, Plus, Edit } from 'lucide-react';
import { Form, Button, Card, Container, Row, Col, Image } from 'react-bootstrap';
import ProfessionalServiceManager from './ProfessionalServiceManager';
import SchedulingCalendar from './SchedulingCalendar';
import { formatPhoneBrazilian, unformatPhone, toUpperCase } from '../utils/formatters';


const ProfessionalForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [formData, setFormData] = useState({ name: '', phone: '', bio: '' });
    const [agendas, setAgendas] = useState<any[]>([]);
    const [selectedAgenda, setSelectedAgenda] = useState<string>('');
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [currentPhoto, setCurrentPhoto] = useState<string | null>(null);
    const [removePhotoFlag, setRemovePhotoFlag] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (id) {
            fetchProfessional();
            fetchAgendas();
        }
    }, [id]);

    const fetchProfessional = async () => {
        try {
            const response = await api.get(`/professionals/${id}/`);
            setFormData(response.data);
            if (response.data.photo) {
                setCurrentPhoto(response.data.photo);
            }
        } catch (error) {
            console.error('Error fetching professional:', error);
        }
    };

    const fetchAgendas = async () => {
        try {
            const response = await api.get(`/agendas/?professional=${id}`);
            setAgendas(response.data);
            if (response.data.length > 0) {
                setSelectedAgenda(String(response.data[0].id));
            }
        } catch (error) {
            console.error('Error fetching agendas:', error);
        }
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhotoFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setPhotoFile(null);
        setPhotoPreview(null);
        if (currentPhoto) {
            setRemovePhotoFlag(true);
            setCurrentPhoto(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const submitData = new FormData();
            submitData.append('name', formData.name.toUpperCase());
            submitData.append('phone', formData.phone);
            submitData.append('bio', formData.bio ? formData.bio.toUpperCase() : '');

            if (id) {
                // Edit mode: normal update
                if (photoFile) {
                    submitData.append('photo', photoFile);
                } else if (removePhotoFlag) {
                    submitData.append('photo', '');
                }

                await api.put(`/professionals/${id}/`, submitData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
            } else {
                // Create mode: Create first, then upload photo
                // 1. Create professional without photo
                const response = await api.post('/professionals/', submitData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                const newId = response.data.id;

                // 2. Upload photo if exists
                if (photoFile) {
                    const photoData = new FormData();
                    photoData.append('photo', photoFile);

                    await api.patch(`/professionals/${newId}/`, photoData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    });
                }
            }
            navigate('/professionals');
        } catch (error) {
            console.error('Error saving professional:', error);
        } finally {
            setLoading(false);
        }
    };

    const displayPhoto = photoPreview || (currentPhoto ? `${currentPhoto}` : null);

    return (
        <Container className="py-2">
            <div className="d-flex align-items-center mb-4">
                <Button
                    variant="link"
                    onClick={() => navigate('/professionals')}
                    className="text-decoration-none text-secondary p-0 me-3 d-flex align-items-center gap-2"
                >
                    <ArrowLeft size={20} /> Voltar
                </Button>
                <h2 className="mb-0">{id ? 'Editar Profissional' : 'Novo Profissional'}</h2>
            </div>

            <Card className="shadow-sm border-0">
                <Card.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={8}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nome</Form.Label>
                                            <Form.Control
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                required
                                                placeholder="Nome completo"
                                                style={{ textTransform: 'uppercase' }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Telefone</Form.Label>
                                            <Form.Control
                                                value={formatPhoneBrazilian(formData.phone)}
                                                onChange={(e) => setFormData({ ...formData, phone: unformatPhone(e.target.value) })}
                                                required
                                                placeholder="(00) 00000-0000"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-4">
                                    <Form.Label>Descrição sobre o profissional</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        value={formData.bio}
                                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                                        placeholder="Breve descrição sobre o profissional..."
                                        style={{ textTransform: 'uppercase' }}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Foto do Profissional</Form.Label>
                                    <div className="text-center">
                                        {displayPhoto ? (
                                            <div
                                                className="position-relative d-inline-block"
                                                onClick={() => fileInputRef.current?.click()}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <Image
                                                    src={displayPhoto}
                                                    alt="Preview"
                                                    roundedCircle
                                                    style={{
                                                        width: '150px',
                                                        height: '150px',
                                                        objectFit: 'cover',
                                                        border: '3px solid #dee2e6'
                                                    }}
                                                />
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    className="position-absolute top-0 end-0 rounded-circle p-1"
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        removePhoto();
                                                    }}
                                                    style={{ width: '30px', height: '30px' }}
                                                >
                                                    <X size={16} />
                                                </Button>
                                            </div>
                                        ) : (
                                            <div
                                                className="d-flex align-items-center justify-content-center bg-light rounded-circle mx-auto"
                                                onClick={() => fileInputRef.current?.click()}
                                                style={{
                                                    width: '150px',
                                                    height: '150px',
                                                    border: '2px dashed #dee2e6',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <Upload size={40} className="text-muted" />
                                            </div>
                                        )}
                                        <Form.Control
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handlePhotoChange}
                                            style={{ display: 'none' }}
                                        />
                                        <Form.Text className="text-muted d-block mt-2">
                                            Clique na imagem para alterar • JPG, PNG, GIF
                                        </Form.Text>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end">
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loading}
                                className="d-flex align-items-center gap-2 px-4"
                            >
                                <Save size={18} /> Salvar
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            {/* Service Manager - Only show when editing existing professional */}
            {id && <ProfessionalServiceManager professionalId={parseInt(id)} />}

            {id && (
                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Configurações da Agenda</h5>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => navigate('/agendas/new')}
                            className="d-flex align-items-center gap-2"
                        >
                            <Plus size={16} /> Nova Configuração
                        </Button>
                    </div>

                    <Card className="shadow-sm border-0 mb-4">
                        <Card.Body className="p-4">
                            <Row className="mb-4">
                                <Col md={8}>
                                    <Form.Label>Configurações Disponíveis</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Select
                                            value={selectedAgenda}
                                            onChange={(e) => setSelectedAgenda(e.target.value)}
                                        >
                                            <option value="">Selecione uma configuração...</option>
                                            {agendas.map((agenda) => (
                                                <option key={agenda.id} value={agenda.id}>
                                                    {agenda.name} ({agenda.start_time} - {agenda.end_time})
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => selectedAgenda && navigate(`/agendas/${selectedAgenda}`)}
                                            disabled={!selectedAgenda}
                                            title="Editar Configuração"
                                        >
                                            <Edit size={18} />
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            <h6 className="mb-3 text-secondary">Pré-visualização e Agendamento</h6>
                            <div style={{ height: '600px' }}>
                                <SchedulingCalendar
                                    professionalId={parseInt(id)}
                                    readOnly={false}
                                    height="100%"
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            )}


        </Container>
    );
};

export default ProfessionalForm;
